/**
 * LES ÉCRITURES DU MODULE : CE QUE DIT UNE RÉPONSE, ET LES CORPS ENVOYÉS.
 * ============================================================================
 *
 * INTENTION : chaque geste dit vrai. Un refus du serveur se dit par sa
 * phrase ; une écriture dont on ne sait pas si elle est passée (délai
 * dépassé, réseau coupé, 503) ne se dit jamais « refusée » ni « rien n'a été
 * modifié » : on ne sait pas, et on relit. Les corps ne portent que ce que
 * la personne a changé, jamais rien de vide, dans la forme que la
 * surveillance porte (une valeur par état des horaires du site, D125).
 *
 * Les codes viennent de la façade (api-metier `origin/staging`,
 * `alarmesv2.ecritures.js`, `alarmesv2.refus.js`, `apialarmes.service.js`,
 * `defaultError.js`, et les routes 5, 6, 7, 8 et 18).
 */
import type { ErreurAlarmesV2 } from "@modules/alarmes-v2/services/appel"
import { CODE_FERMEE } from "@modules/alarmes-v2/services/appel"
import { cleDuRefus } from "@modules/alarmes-v2/utils/refus"
import type {
    AlarmesV2Canal,
    AlarmesV2Destinataire,
    AlarmesV2DestinataireEcrit,
    AlarmesV2ValeurChoisie,
} from "@modules/alarmes-v2/types/alarmes-v2"

/**
 * Vrai quand on ne sait pas si l'écriture est passée : pas de réponse (délai
 * dépassé, réseau coupé) ou une réponse 5xx (`alarmes_indisponible` : « un
 * délai ne prouve pas qu'elle n'a pas été enregistrée »).
 */
export function estIncertaine(erreur: ErreurAlarmesV2): boolean {
    return erreur.statut === null || erreur.statut >= 500
}

/** Les codes d'un refus d'écriture qui ont leur phrase, par clé de texte. */
const PHRASE_DU_CODE: Record<string, string> = {
    required_alarme_avancee: "alarmesV2.actions.refus.avance",
    alarmes_episode_revenu: "alarmesV2.actions.refus.alarmeRevenue",
    alarmes_pour_information: "alarmesV2.actions.refus.pourInformation",
    pas_destinataire: "alarmesV2.actions.refus.pasDestinataire",
    cap_test_atteint: "alarmesV2.actions.refus.capTest",
    destinataire_inconnu: "alarmesV2.actions.refus.destinataireInconnu",
    canal_hors_destinataire: "alarmesV2.actions.refus.canalHorsDestinataire",
    avis_hors_ecart: "alarmesV2.actions.refus.avisHorsEcart",
    valeurs_nombres_invalides: "alarmesV2.actions.refus.valeursInvalides",
    alarmes_corps_invalide: "alarmesV2.actions.refus.forme",
    alarmes_corps_vide: "alarmesV2.actions.refus.forme",
    alarmes_corps_refuse: "alarmesV2.actions.refus.forme",
    instance_inconnue: "alarmesV2.refus.neutre",
    occurrence_inconnue: "alarmesV2.refus.neutre",
}

/**
 * Les refus de la route 8 dont le message est écrit pour l'écran et
 * s'affiche tel quel (`user._id_user.phone.put.route.js`).
 */
const CODES_DU_NUMERO = new Set([
    "alarmes_numero_manquant",
    "alarmes_numero_non_reconnu",
    "alarmes_numero_sans_sms",
    "alarmes_membre_hors_entreprise",
])

/** Ce qu'un refus fait dire à l'écran : une clé de texte, ou un texte servi. */
export interface PhraseDuRefus {
    cle?: string
    texte?: string
}

/**
 * La phrase d'un refus d'écriture (un 4xx : le serveur a répondu non, rien
 * n'est écrit). Un 403 motivé se dit par la phrase de son motif, même si le
 * bouton était actif à l'écran.
 * @param erreur - le refus servi
 */
export function phraseDuRefus(erreur: ErreurAlarmesV2): PhraseDuRefus {
    if (CODES_DU_NUMERO.has(erreur.code)) {
        return erreur.message && erreur.message !== erreur.code
            ? { texte: erreur.message }
            : { cle: "alarmesV2.actions.refus.numero" }
    }
    if (erreur.code === CODE_FERMEE) return { cle: "alarmesV2.refus.fermee" }
    const parCode =
        PHRASE_DU_CODE[erreur.code] ??
        (erreur.motif ? PHRASE_DU_CODE[erreur.motif] : undefined)
    if (parCode) return { cle: parCode }
    if (erreur.statut === 403) return { cle: cleDuRefus(erreur) }
    return { cle: "alarmesV2.actions.refus.generique" }
}

// ------------------------------------------------------------ les seuils

/** Une valeur saisie dans le dialogue des paramètres : un texte de champ. */
export type Saisie = string

/** Les saisies d'un nombre : une seule, ou une par état du site. */
export type SaisiesDUnNombre =
    | { forme: "simple"; valeur: Saisie }
    | { forme: "profil"; ouvert: Saisie; ferme: Saisie }

/** Le résultat de la lecture des saisies. */
export type LectureParametres =
    | { ok: true; valeurs: Record<string, AlarmesV2ValeurChoisie> | null }
    | { ok: false; erreurs: Record<string, string> }

/**
 * Une saisie en nombre. `""` rend `null` ; autre chose qu'un nombre fini rend
 * `undefined` (illisible). La virgule décimale est admise.
 */
export function lireNombre(saisie: Saisie): number | null | undefined {
    const brut = saisie.trim().replace(",", ".")
    if (brut === "") return null
    const n = Number(brut)
    return Number.isFinite(n) ? n : undefined
}

/** Le texte de champ d'une valeur servie, `""` pour `null`. */
export function texteDuNombre(v: number | null | undefined): Saisie {
    return typeof v === "number" ? String(v) : ""
}

/**
 * Les saisies de départ, depuis la forme que la surveillance porte : un
 * nombre ou `null` donne un champ, `{ouvert, ferme}` en donne deux. Une
 * autre forme (un code, une valeur par un autre état) n'est pas réglable ici.
 * @param valeurs - `valeurs_nombres` de la surveillance
 * @param cles - les clés que l'écran sait nommer
 */
export function saisiesDeDepart(
    valeurs: Record<string, AlarmesV2ValeurChoisie> | null | undefined,
    cles: readonly string[]
): Record<string, SaisiesDUnNombre> {
    const sortie: Record<string, SaisiesDUnNombre> = {}
    if (!valeurs) return sortie
    for (const cle of cles) {
        if (!Object.prototype.hasOwnProperty.call(valeurs, cle)) continue
        const v = valeurs[cle]
        if (v === null || typeof v === "number") {
            sortie[cle] = { forme: "simple", valeur: texteDuNombre(v) }
        } else if (
            v &&
            typeof v === "object" &&
            Object.keys(v).every((k) => k === "ouvert" || k === "ferme") &&
            Object.keys(v).length > 0
        ) {
            sortie[cle] = {
                forme: "profil",
                ouvert: texteDuNombre(v.ouvert),
                ferme: texteDuNombre(v.ferme),
            }
        }
    }
    return sortie
}

/**
 * Le `valeurs_nombres` à envoyer : la map entière que la surveillance porte,
 * chaque valeur réglée remplacée, toute autre recopiée telle quelle ; `null`
 * quand rien n'a changé (l'écran n'envoie jamais un corps vide). Un champ
 * illisible, ou vidé alors qu'une valeur était posée, est refusé à l'écran.
 * @param valeurs - `valeurs_nombres` de la surveillance
 * @param saisies - ce que la personne a saisi, par clé
 * @param messages - les phrases d'erreur
 */
export function lireParametres(
    valeurs: Record<string, AlarmesV2ValeurChoisie>,
    saisies: Record<string, SaisiesDUnNombre>,
    messages: { illisible: string; vide: string }
): LectureParametres {
    const erreurs: Record<string, string> = {}
    const sortie: Record<string, AlarmesV2ValeurChoisie> = { ...valeurs }
    let change = false
    const un = (
        champ: string,
        saisie: Saisie,
        avant: number | null | undefined
    ): number | null | undefined => {
        const lu = lireNombre(saisie)
        if (lu === undefined) {
            erreurs[champ] = messages.illisible
            return undefined
        }
        if (lu === null && typeof avant === "number") {
            erreurs[champ] = messages.vide
            return undefined
        }
        return lu
    }
    for (const [cle, s] of Object.entries(saisies)) {
        const avant = valeurs[cle]
        if (s.forme === "simple") {
            const n = un(cle, s.valeur, avant as number | null)
            if (n === undefined) continue
            if (n !== avant) {
                sortie[cle] = n
                change = true
            }
        } else {
            const parEtat = (avant ?? {}) as Record<string, number | null>
            const ouvert = un(`${cle}.ouvert`, s.ouvert, parEtat.ouvert)
            const ferme = un(`${cle}.ferme`, s.ferme, parEtat.ferme)
            if (ouvert === undefined || ferme === undefined) continue
            if (ouvert !== parEtat.ouvert || ferme !== parEtat.ferme) {
                sortie[cle] = { ...parEtat, ouvert, ferme }
                change = true
            }
        }
    }
    if (Object.keys(erreurs).length > 0) return { ok: false, erreurs }
    return { ok: true, valeurs: change ? sortie : null }
}

// ------------------------------------------------------ les destinataires

/** Les canaux servis d'un destinataire, réduits à ceux que la façade admet. */
function canauxEcrits(
    canaux: string[] | null | undefined
): AlarmesV2Canal[] | null | undefined {
    if (canaux === undefined) return undefined
    if (canaux === null) return null
    const lus = [...new Set(canaux)].filter(
        (c): c is AlarmesV2Canal => c === "sms" || c === "mail"
    )
    return lus.length > 0 ? lus : null
}

/**
 * Les destinataires servis, dans la forme que la route 5 admet ; `null` si
 * un destinataire n'est pas lisible (on ne réécrit pas une liste qu'on ne
 * sait pas relire en entier).
 */
export function destinatairesEcrits(
    destinataires: AlarmesV2Destinataire[]
): AlarmesV2DestinataireEcrit[] | null {
    const sortie: AlarmesV2DestinataireEcrit[] = []
    for (const d of destinataires) {
        if (!d || typeof d._id_user !== "string" || d._id_user === "")
            return null
        const canaux = canauxEcrits(d.canaux)
        sortie.push(
            canaux === undefined
                ? { _id_user: d._id_user }
                : { _id_user: d._id_user, canaux }
        )
    }
    return sortie
}

/**
 * La liste à envoyer pour « M'inscrire » (par mail, S:5582) ou « Me
 * retirer » ; `null` si la liste servie n'est pas lisible ou si rien ne
 * change.
 * @param destinataires - la liste servie
 * @param moi - l'identifiant de la personne connectée
 * @param inscrire - vrai pour s'inscrire, faux pour se retirer
 */
export function destinatairesPourMoi(
    destinataires: AlarmesV2Destinataire[],
    moi: string,
    inscrire: boolean
): AlarmesV2DestinataireEcrit[] | null {
    const ecrits = destinatairesEcrits(destinataires)
    if (!ecrits || !moi) return null
    const present = ecrits.some((d) => d._id_user === moi)
    if (inscrire === present) return null
    return inscrire
        ? [...ecrits, { _id_user: moi, canaux: ["mail"] }]
        : ecrits.filter((d) => d._id_user !== moi)
}

/**
 * Vrai quand deux listes de destinataires disent la même chose (mêmes
 * personnes, mêmes canaux, dans n'importe quel ordre).
 */
export function memesDestinataires(
    a: AlarmesV2DestinataireEcrit[],
    b: AlarmesV2DestinataireEcrit[]
): boolean {
    const cle = (d: AlarmesV2DestinataireEcrit) =>
        `${d._id_user}:${d.canaux === undefined ? "?" : d.canaux === null ? "-" : [...d.canaux].sort().join("+")}`
    const ca = a.map(cle).sort()
    const cb = b.map(cle).sort()
    return ca.length === cb.length && ca.every((x, i) => x === cb[i])
}
